/**
 * 지식 베이스 서비스
 * Firestore CRUD + Web Crypto API 암호화
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { encryptData, decryptData } from './webCryptoService';

const COLLECTION_NAME = 'knowledge_base';

/**
 * 지식 카테고리 정의
 */
export const KNOWLEDGE_CATEGORIES = {
  account_tips: { label: '계정 관리 팁', icon: '👤', color: '#3b82f6' },
  security_tips: { label: '보안 팁', icon: '🔒', color: '#ef4444' },
  general_notes: { label: '일반 메모', icon: '📝', color: '#10b981' },
  references: { label: '참고 자료', icon: '📚', color: '#8b5cf6' },
  faq: { label: 'FAQ', icon: '❓', color: '#f59e0b' }
};

/**
 * 지식 항목 생성
 */
export async function createKnowledge(userId, data) {
  try {
    const { title, content, category, tags = [], priority = 5 } = data;

    // 민감 데이터 암호화
    const encryptedTitle = await encryptData(title, userId);
    const encryptedContent = await encryptData(content, userId);
    const encryptedTags = tags.length > 0
      ? await encryptData(JSON.stringify(tags), userId)
      : null;

    const docData = {
      userId,
      title: encryptedTitle,
      content: encryptedContent,
      category,
      tags: encryptedTags,
      priority,
      isActive: true,
      isFavorite: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), docData);

    // 생성된 문서 반환
    const createdDoc = await getDoc(docRef);
    const createdData = createdDoc.data();
    const decrypted = await decryptKnowledgeData(createdData, userId);

    return { id: docRef.id, ...decrypted };
  } catch (error) {
    console.error('Error creating knowledge:', error);
    throw error;
  }
}

/**
 * 지식 항목 조회
 */
export async function getKnowledge(knowledgeId, userId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, knowledgeId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Knowledge not found');
    }

    const data = docSnap.data();

    if (data.userId !== userId) {
      throw new Error('Unauthorized access');
    }

    const decrypted = await decryptKnowledgeData(data, userId);
    return { id: docSnap.id, ...decrypted };
  } catch (error) {
    console.error('Error getting knowledge:', error);
    throw error;
  }
}

/**
 * 지식 목록 조회
 */
export async function getKnowledgeList(userId, filters = {}) {
  try {
    // 기본 쿼리 - userId만으로 필터 (인덱스 불필요)
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);
    let items = [];

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const decrypted = await decryptKnowledgeData(data, userId);
      items.push({ id: docSnap.id, ...decrypted });
    }

    // 클라이언트 측 필터링
    if (filters.category) {
      items = items.filter(item => item.category === filters.category);
    }

    if (filters.isActive !== undefined) {
      items = items.filter(item => item.isActive === filters.isActive);
    }

    // 클라이언트 측 정렬
    const sortBy = filters.sortBy || 'updatedAt';
    const sortOrder = filters.sortOrder || 'desc';

    items.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Timestamp 처리
      if (aVal?.toDate) aVal = aVal.toDate();
      if (bVal?.toDate) bVal = bVal.toDate();

      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : -1;
      }
      return aVal > bVal ? 1 : -1;
    });

    return items;
  } catch (error) {
    console.error('Error getting knowledge list:', error);
    throw error;
  }
}

/**
 * 지식 항목 업데이트
 */
export async function updateKnowledge(knowledgeId, userId, updates) {
  try {
    const docRef = doc(db, COLLECTION_NAME, knowledgeId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Knowledge not found');
    }

    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized access');
    }

    const encryptedUpdates = { ...updates };

    // 암호화가 필요한 필드 처리
    if (updates.title) {
      encryptedUpdates.title = await encryptData(updates.title, userId);
    }

    if (updates.content) {
      encryptedUpdates.content = await encryptData(updates.content, userId);
    }

    if (updates.tags) {
      encryptedUpdates.tags = updates.tags.length > 0
        ? await encryptData(JSON.stringify(updates.tags), userId)
        : null;
    }

    encryptedUpdates.updatedAt = serverTimestamp();

    await updateDoc(docRef, encryptedUpdates);

    // 업데이트된 문서 반환
    const updatedDoc = await getDoc(docRef);
    const updatedData = updatedDoc.data();
    const decrypted = await decryptKnowledgeData(updatedData, userId);

    return { id: knowledgeId, ...decrypted };
  } catch (error) {
    console.error('Error updating knowledge:', error);
    throw error;
  }
}

/**
 * 지식 항목 삭제
 */
export async function deleteKnowledge(knowledgeId, userId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, knowledgeId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Knowledge not found');
    }

    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized access');
    }

    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting knowledge:', error);
    throw error;
  }
}

/**
 * 즐겨찾기 토글
 */
export async function toggleKnowledgeFavorite(knowledgeId, userId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, knowledgeId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Knowledge not found');
    }

    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized access');
    }

    const currentFavorite = docSnap.data().isFavorite || false;

    await updateDoc(docRef, {
      isFavorite: !currentFavorite,
      updatedAt: serverTimestamp()
    });

    return !currentFavorite;
  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
}

/**
 * 활성화 토글
 */
export async function toggleKnowledgeActive(knowledgeId, userId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, knowledgeId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Knowledge not found');
    }

    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized access');
    }

    const currentActive = docSnap.data().isActive !== false;

    await updateDoc(docRef, {
      isActive: !currentActive,
      updatedAt: serverTimestamp()
    });

    return !currentActive;
  } catch (error) {
    console.error('Error toggling active:', error);
    throw error;
  }
}

/**
 * 지식 검색
 */
export async function searchKnowledge(userId, searchTerm) {
  try {
    const items = await getKnowledgeList(userId, { isActive: true });

    if (!searchTerm) return items;

    const lowerSearch = searchTerm.toLowerCase();

    return items.filter(item => {
      const titleMatch = item.title?.toLowerCase().includes(lowerSearch);
      const contentMatch = item.content?.toLowerCase().includes(lowerSearch);
      const tagMatch = item.tags?.some(tag =>
        tag.toLowerCase().includes(lowerSearch)
      );

      return titleMatch || contentMatch || tagMatch;
    });
  } catch (error) {
    console.error('Error searching knowledge:', error);
    throw error;
  }
}

/**
 * 데이터 복호화 헬퍼
 */
async function decryptKnowledgeData(data, userId) {
  try {
    const decrypted = { ...data };

    // title 복호화
    if (data.title && typeof data.title === 'object') {
      decrypted.title = await decryptData(data.title, userId);
    }

    // content 복호화
    if (data.content && typeof data.content === 'object') {
      decrypted.content = await decryptData(data.content, userId);
    }

    // tags 복호화
    if (data.tags && typeof data.tags === 'object' && !Array.isArray(data.tags)) {
      const tagsStr = await decryptData(data.tags, userId);
      decrypted.tags = JSON.parse(tagsStr);
    } else if (!data.tags) {
      decrypted.tags = [];
    }

    return decrypted;
  } catch (error) {
    console.error('Error decrypting knowledge data:', error);
    throw error;
  }
}
